import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen } from "lucide-react";
import api from "@/lib/api";

const KIND = {
  pack: { es: "Forfait", en: "Pack", fr: "Forfait" },
  booking: { es: "Clase", en: "Class", fr: "Cours" },
  booking_no_credit: { es: "Clase SIN descuento", en: "Class WITHOUT credit", fr: "Cours SANS décompte" },
  refund: { es: "Devolución", en: "Refund", fr: "Remboursement" },
  adjust: { es: "Ajuste admin", en: "Admin adjust", fr: "Ajustement admin" },
  reassigned: { es: "Reasignado", en: "Reassigned", fr: "Réassigné" },
};

export function CreditLedgerSection({ userId }) {
  const { tl } = useI18n();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState("");
  const [data, setData] = useState(null);
  const load = (date) => api.get(`/admin/clients/${userId}/ledger`, { params: date ? { at: date } : {} }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (open) load(at); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);
  const fmt = (ts) => (ts ? new Date(ts).toLocaleString("es-PA", { timeZone: "America/Panama", dateStyle: "short", timeStyle: "short" }) : "");
  return (
    <div data-testid="credit-ledger">
      <button data-testid="credit-ledger-toggle" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-sm font-semibold hover:underline">
        <BookOpen className="h-4 w-4 text-primary" />{tl({ es: "Historial de créditos (auditoría)", en: "Credit history (audit)", fr: "Historique des crédits (audit)" })}
      </button>
      {open && data && (
        <div className="mt-3 space-y-3 rounded-xl border border-border bg-secondary/30 p-3 text-sm">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Saldo al inicio del día", en: "Balance at start of day", fr: "Solde au début du jour" })}</label>
              <Input data-testid="ledger-date" type="date" value={at} onChange={(e) => setAt(e.target.value)} className="h-9 w-44" />
            </div>
            <Button data-testid="ledger-date-btn" size="sm" variant="outline" className="h-9 rounded-full" onClick={() => load(at)}>{tl({ es: "Calcular", en: "Compute", fr: "Calculer" })}</Button>
            {data.at && (
              <div data-testid="ledger-balance-at" className="rounded-lg bg-primary/10 px-3 py-1.5 font-semibold text-primary">
                {data.at}: {data.balance_at} {tl({ es: "créditos", en: "credits", fr: "crédits" })}
                {data.active_packs_at?.length ? <span className="ml-1 font-normal text-muted-foreground">({data.active_packs_at.join(", ")})</span> : null}
              </div>
            )}
            <div className="ml-auto text-xs text-muted-foreground">{tl({ es: "Saldo actual", en: "Current balance", fr: "Solde actuel" })}: <b data-testid="ledger-current">{data.current_balance}</b></div>
          </div>
          {data.packs.some((p) => p.gap !== 0) && (
            <div data-testid="ledger-gap" className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {tl({ es: "Diferencia detectada entre el saldo guardado y el historial:", en: "Gap detected between stored balance and history:", fr: "Écart détecté entre le solde enregistré et l'historique :" })}{" "}
              {data.packs.filter((p) => p.gap !== 0).map((p) => `${p.label}: ${p.stored_remaining} vs ${p.ledger_remaining} (${p.gap > 0 ? "+" : ""}${p.gap})`).join(" · ")}
            </div>
          )}
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground"><tr><th className="py-1 pr-2">{tl({ es: "Fecha", en: "Date", fr: "Date" })}</th><th className="py-1 pr-2">{tl({ es: "Movimiento", en: "Event", fr: "Mouvement" })}</th><th className="py-1 pr-2 text-right">+/−</th><th className="py-1 text-right">{tl({ es: "Saldo", en: "Balance", fr: "Solde" })}</th></tr></thead>
              <tbody>
                {data.events.map((e, i) => (
                  <tr key={`${e.kind}-${e.reservation_id || e.credit_id || i}-${e.ts}`} data-testid={`ledger-row-${i}`} className={`border-t border-border/60 ${e.kind === "booking_no_credit" ? "text-destructive" : ""}`}>
                    <td className="whitespace-nowrap py-1 pr-2 text-muted-foreground">{fmt(e.ts)}</td>
                    <td className="py-1 pr-2"><span className="font-medium">{tl(KIND[e.kind] || { es: e.kind })}</span> · {e.label}{e.status && e.kind !== "pack" ? <span className="text-muted-foreground"> ({e.status})</span> : null}</td>
                    <td className={`py-1 pr-2 text-right font-semibold ${e.delta > 0 ? "text-emerald-600" : e.delta < 0 ? "text-destructive" : "text-muted-foreground"}`}>{e.delta > 0 ? `+${e.delta}` : e.delta}</td>
                    <td className="py-1 text-right">{e.balance}</td>
                  </tr>
                ))}
                {data.events.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
